import React, { useState, forwardRef, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  TextField,
  Grid,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { maxLengthValidation } from '../../utils/validations.util';
import Iconify from '../common/iconify.component';
import SnackbarInfo from '../common/snackbar-info.component';
import { createLGNewData } from '../../services/channel-packager';
import StyledDialogActions from '../../theme/dialogActionStyles';
import S3ImageUpload from '../common/S3ImageUpload';

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const AddChannelLGPackager = ({
  title,
  channelId,
  channelCode,
  onCancel,
  onSuccess,
}) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm({
    defaultValues: {
      version: '',
      notes: '',
    },
  });

  const packageUrl = useRef(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const saveNewData = (payload) => {
    createLGNewData(payload)
      .then(() => {
        onSuccess('New package uploaded successfully.');
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });
    if (packageUrl.current) {
      const payload = {
        name: packageUrl.current.split('/')[
          packageUrl.current.split('/').length - 1
        ],
        channelId,
        platform: 2,
        version: data.version,
        notes: data.notes,
        url: packageUrl.current,
      };
      saveNewData(payload);
    } else {
      setFormSubmitted(false);
      setSnackbarInfo({
        show: true,
        type: 'error',
        message: 'Please upload package file to proceed.',
      });
    }
  };

  const updateImage = (url) => {
    packageUrl.current = url;
  };

  return (
    <Dialog
      open
      aria-labelledby="add-dialog-title"
      aria-describedby="add-dialog-description"
      TransitionComponent={Transition}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle id="add-dialog-title">{title}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onCancel}
        size="small"
        sx={{
          position: 'absolute',
          right: 10,
          top: 15,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Iconify icon="ic:outline-close" />
      </IconButton>

      <DialogContent dividers id="add-dialog-description">
        <form id="add-packager-form" onSubmit={handleSubmit(onFormSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12}>
              <Controller
                name="version"
                control={control}
                rules={{
                  ...maxLengthValidation(20),
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Version"
                    type="text"
                    error={!!errors.title}
                    helperText={errors?.title?.message || null}
                    fullWidth
                    variant="filled"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
              <Controller
                name="notes"
                control={control}
                rules={{
                  ...maxLengthValidation(255),
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Release Notes"
                    type="text"
                    error={!!errors.slug}
                    helperText={errors?.slug?.message || null}
                    fullWidth
                    variant="filled"
                  />
                )}
              />
            </Grid>
          </Grid>
        </form>
        <Grid item xs={12} sm={12} md={12} mt={5}>
          <S3ImageUpload
            s3Folder={`${channelCode}/LG`}
            setSnackbarInfo={setSnackbarInfo}
            updateImage={updateImage}
            isLG
          />
        </Grid>
      </DialogContent>

      <StyledDialogActions>
        <Grid>
          <Button
            type="submit"
            form="add-packager-form"
            color="primary"
            variant="contained"
            disabled={formSubmitted}
            sx={{ marginRight: 2 }}
          >
            Save
          </Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={onCancel}
            disabled={formSubmitted}
          >
            Cancel
          </Button>
        </Grid>
      </StyledDialogActions>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Dialog>
  );
};

AddChannelLGPackager.propTypes = {
  title: PropTypes.string.isRequired,
  channelId: PropTypes.number.isRequired,
  channelCode: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default AddChannelLGPackager;
